import type { ValueType } from './ir.js';
import { elemOf } from './ir.js';

/** A single value, or a list (or table) of them. */
export type Value = number | string | boolean | Value[];

/** Places shown before "…" when a list appears in the watch or a loop trace. */
const WATCH_ELEMENTS = 8;

/**
 * A double printed the way `cout << x` prints it by default (`%g`, 6 significant digits):
 * 2.5 → "2.5", 1/3 → "0.333333", 5.0 → "5", 1e6 → "1e+06".
 */
export function formatDouble(x: number): string {
  if (Number.isNaN(x)) return 'nan';
  if (!Number.isFinite(x)) return x > 0 ? 'inf' : '-inf';
  if (x === 0) return Object.is(x, -0) ? '-0' : '0';
  const [mantissa = '0', expPart = '0'] = x.toExponential(5).split('e');
  const exp = Number(expPart);
  if (exp < -4 || exp >= 6) {
    const m = mantissa.replace(/\.?0+$/, '');
    const sign = exp < 0 ? '-' : '+';
    return `${m}e${sign}${String(Math.abs(exp)).padStart(2, '0')}`;
  }
  return x
    .toFixed(Math.max(0, 5 - exp))
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
}

/** How `print` shows a value of a given static type (C++ reference semantics). */
export function formatValue(v: Value, type: ValueType): string {
  if (Array.isArray(v)) return formatList(v, type, Infinity);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'string') return v;
  if (type === 'int' || (Number.isInteger(v) && type !== 'double')) return String(Math.trunc(v));
  return formatDouble(v);
}

/** A value as shown in the variable watch: text in quotes, long lists cut short. */
export function formatWatch(v: Value, type: ValueType): string {
  if (Array.isArray(v)) return formatList(v, type, WATCH_ELEMENTS);
  if (typeof v === 'string') return type === 'char' ? `'${v}'` : JSON.stringify(v);
  return formatValue(v, type);
}

/** `[1, 2, 3]`, nested for a table. The watch cuts it short so the status bar stays readable. */
function formatList(v: Value[], type: ValueType, max: number): string {
  const shown = v
    .slice(0, max)
    .map((x) => (Array.isArray(x) ? formatList(x, type, max) : formatWatch(x, elemOf(type))));
  if (v.length > max) shown.push('…');
  return `[${shown.join(', ')}]`;
}
